import PreparationSourceAttribution from './PreparationSourceAttribution';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Checkbox, IconButton, Menu, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import { MoreHorizRounded } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import colors from '../../assets/theme/colors';
import { JOURNEY_ANSWER_STATUS } from './journeyAnswerModel';
import AnswerDisplayText from './AnswerDisplayText';

const actionSx = { minWidth: 0, textTransform: 'none', fontWeight: 700 };
const isOutsideProviderItem = (question = {}) => (question.requestedEvidenceIntentRefs || [])
  .some((ref) => (ref?.intentId || ref) === 'providers_care_team');

const GuidedPreparationQuestion = ({
  item, index, canEdit, accent, saving, onConfirmSuggested, onSaveOwn, onSkip, onRestore, onViewSources,
  presentation = 'default', evidence = [],
}) => {
  const {
    question, proposed, answer, freshness,
  } = item;
  const [mode, setMode] = useState('view');
  const [text, setText] = useState('');
  const [selectedPoints, setSelectedPoints] = useState([]);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const isConfirmed = answer?.status === JOURNEY_ANSWER_STATUS.CONFIRMED;
  const isSkipped = answer?.status === JOURNEY_ANSWER_STATUS.SKIPPED;
  const itemType = question.itemType || question.answerabilityMode || 'records_answerable';
  const isPreparedInformation = itemType === 'prepared_information';
  const isCaregiverOnly = itemType === 'caregiver_only';
  const isCustomCard = presentation === 'custom_cards';
  const points = useMemo(() => proposed?.candidatePoints || [], [proposed?.candidatePoints]);
  const suggestedText = proposed?.proposedText || '';
  const selectedSourceIds = useMemo(() => [...new Set(
    selectedPoints.flatMap((pointIndex) => points[pointIndex]?.sourceIds || [])
  )], [points, selectedPoints]);
  const defaultSourceIds = answer ? answer.sourceIds || [] : proposed?.sourceIds || [];
  const customMenuSourceIds = [...new Set([
    ...(answer?.sourceIds || []), ...(proposed?.sourceIds || []),
  ])];
  const currentProposed = freshness ? {
    ...(proposed || {}),
    evidenceFingerprint: freshness.currentEvidenceFingerprint,
    evidenceSourceIds: freshness.evidenceSourceIds || freshness.sourceIds || [],
  } : proposed;
  const currentSourceIds = freshness ? [...new Set([
    ...defaultSourceIds, ...(freshness.sourceIds || []),
  ])] : defaultSourceIds;

  useEffect(() => {
    setMode('view');
    setText(answer?.confirmedText || suggestedText || '');
    setSelectedPoints([]);
  }, [answer?.confirmedText, answer?.status, suggestedText]);

  const startEdit = () => {
    const selectedText = selectedPoints.map((pointIndex) => points[pointIndex]?.text).filter(Boolean).join('\n\n');
    setText(answer?.confirmedText || selectedText || suggestedText || '');
    setMode('edit');
  };
  const startOwn = () => {
    setText(answer?.origin === 'caregiver_authored' ? answer.confirmedText : '');
    setMode('own');
  };
  const confirmText = () => (answer?.origin === 'caregiver_authored' ? onSaveOwn({
    questionId: question.id,
    proposed: currentProposed,
    text: text.trim(),
  }) : onConfirmSuggested({
    questionId: question.id,
    proposed: currentProposed,
    text: text.trim(),
    sourceIds: selectedSourceIds.length ? selectedSourceIds : currentSourceIds,
  })).then((saved) => saved && setMode('view'));
  const confirmPoints = () => {
    const selectedText = selectedPoints.map((pointIndex) => points[pointIndex]?.text).filter(Boolean).join('\n\n');
    return onConfirmSuggested({
      questionId: question.id, proposed: currentProposed, text: selectedText, sourceIds: selectedSourceIds,
    });
  };
  const closeMenuThen = (action) => {
    setMenuAnchor(null);
    action();
  };

  const statusLabel = isConfirmed
    ? 'Added to brief'
    : isSkipped
      ? 'Left out'
      : suggestedText || points.length
        ? 'Suggested by Lifelog'
        : 'Needs your input';

  return (
    <Box data-testid={`journey-question-${question.id}`} sx={{
      p: isCustomCard ? { xs: 1.6, sm: 2, md: 2.4 } : { xs: 1.4, md: 1.8 },
      ...(isCustomCard ? {
        bgcolor: '#FFFFFF', borderRadius: { xs: '20px', md: '24px' },
        border: `1px solid ${alpha(accent.primary, 0.15)}`,
        borderTop: `4px solid ${accent.primary}`,
        boxShadow: `0 8px 24px ${alpha(colors.semantic.ink, 0.06)}`,
        ...(isSkipped ? { opacity: 0.72, py: { xs: 1.2, md: 1.4 } } : {}),
      } : { '&:not(:last-child)': { borderBottom: `1px solid ${alpha(colors.semantic.ink, 0.1)}` } }),
    }}>
      <Typography sx={{ fontSize: '0.7rem', fontWeight: 750, letterSpacing: '0.08em', textTransform: 'uppercase', color: accent.ink }}>
        {isPreparedInformation ? 'Prepared information' : `Preparation item ${index + 1}`}
      </Typography>
      <Typography component="h3" sx={{ mt: 0.25, fontSize: { xs: '1rem', md: '1.08rem' }, fontWeight: 750, lineHeight: 1.35, color: colors.semantic.ink }}>
        {question.prompt}
      </Typography>
      {isCustomCard && !isSkipped ? (
        <Typography sx={{ mt: 0.45, fontSize: '0.76rem', fontWeight: 650, color: isConfirmed ? accent.ink : alpha(colors.semantic.ink, 0.56) }}>
          {isConfirmed ? 'Included' : statusLabel}
        </Typography>
      ) : null}

      <PreparationSourceAttribution sourceIds={defaultSourceIds} evidence={evidence} />
      {isCustomCard && isSkipped && mode === 'view' ? (
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 0.65 }}>
          <Typography sx={{ fontSize: '0.82rem', color: alpha(colors.semantic.ink, 0.62) }}>Left out</Typography>
          <Button
            disabled={saving}
            onClick={() => onRestore({ questionId: question.id, proposed: currentProposed })}
            sx={actionSx}
          >Add back</Button>
        </Stack>
      ) : null}

      {(!isSkipped || !isCustomCard) && freshness ? (
        <Box sx={{ mt: 0.8, px: 1, py: 0.75, borderRadius: '12px', bgcolor: alpha(accent.primary, 0.08) }}>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 750, color: accent.ink }}>
            New information is available
          </Typography>
          {freshness.sourceIds?.length ? (
            <Button disabled={saving} onClick={() => onViewSources(freshness.sourceIds)} sx={{ ...actionSx, mt: 0.15, p: 0 }}>
              View new sources
            </Button>
          ) : null}
        </Box>
      ) : null}

      {!isSkipped && isConfirmed && mode === 'view' ? (
        <Box sx={{ mt: 1, p: 1.1, borderRadius: '14px', bgcolor: alpha(accent.primary, 0.08) }}>
          <Typography sx={{ fontSize: '0.76rem', fontWeight: 750, color: accent.ink }}>Ready to use</Typography>
          <AnswerDisplayText sx={{ mt: 0.35, fontSize: '0.9rem', lineHeight: 1.5 }}>{answer.confirmedText}</AnswerDisplayText>
        </Box>
      ) : !isCustomCard && isSkipped && mode === 'view' ? (
        <Typography sx={{ mt: 0.8, fontSize: '0.86rem', color: alpha(colors.semantic.ink, 0.58) }}>You chose to leave this question blank.</Typography>
      ) : !isSkipped && points.length && mode === 'view' ? (
        <Stack spacing={0.45} sx={{ mt: 0.8 }}>
          <Typography sx={{ fontSize: '0.82rem', color: alpha(colors.semantic.ink, 0.68) }}>Choose any points that feel important to you.</Typography>
          {points.map((point, pointIndex) => (
            <Box key={`${question.id}-${pointIndex}`} sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <Checkbox
                size="small"
                checked={selectedPoints.includes(pointIndex)}
                onChange={() => setSelectedPoints((current) => current.includes(pointIndex)
                  ? current.filter((value) => value !== pointIndex) : [...current, pointIndex])}
                inputProps={{ 'aria-label': point.text }}
              />
              <Typography sx={{ pt: 0.7, fontSize: '0.88rem', lineHeight: 1.45 }}>{point.text}</Typography>
            </Box>
          ))}
        </Stack>
      ) : !isSkipped && suggestedText && mode === 'view' ? (
        <AnswerDisplayText sx={{ mt: 0.8, fontSize: '0.9rem', lineHeight: 1.5, color: alpha(colors.semantic.ink, 0.82) }}>{suggestedText}</AnswerDisplayText>
      ) : !isSkipped && mode === 'view' ? (
        <Typography sx={{ mt: 0.8, fontSize: '0.86rem', color: alpha(colors.semantic.ink, 0.58) }}>
          {isCaregiverOnly
            ? 'Add what matters to you, or leave this blank.'
            : isCustomCard && isOutsideProviderItem(question)
              ? 'No outside providers or therapies were found in Lifelog. You can add one or leave this out.'
              : 'Lifelog has not found enough information for this item yet. You can add what is missing or leave it out.'}
        </Typography>
      ) : null}

      {!isSkipped && mode !== 'view' ? (
        <Stack spacing={0.7} sx={{ mt: 1 }}>
          <TextField
            value={text}
            onChange={(event) => setText(event.target.value)}
            multiline minRows={3} fullWidth autoFocus
            label={mode === 'own' ? 'Write your answer' : 'Edit answer'}
          />
          <Stack direction="row" spacing={0.5}>
            <Button disabled={saving || !text.trim()} onClick={mode === 'own'
              ? () => onSaveOwn({ questionId: question.id, proposed: currentProposed, text: text.trim() }).then((saved) => saved && setMode('view'))
              : confirmText} sx={actionSx}>Save answer</Button>
            <Button disabled={saving} onClick={() => setMode('view')} sx={{ ...actionSx, color: alpha(colors.semantic.ink, 0.58) }}>Cancel</Button>
          </Stack>
        </Stack>
      ) : canEdit && isCustomCard && !isSkipped ? (
        <Stack direction="row" alignItems="center" spacing={0.7} sx={{ mt: { xs: 1.2, md: 1.45 } }}>
          {!isConfirmed && (suggestedText || selectedPoints.length) ? (
            <Button disabled={Boolean(saving || (points.length && !selectedPoints.length))} onClick={points.length ? confirmPoints : () => onConfirmSuggested({
              questionId: question.id, proposed: currentProposed, text: suggestedText, sourceIds: currentSourceIds,
            })} sx={actionSx}>Use this</Button>
          ) : !isConfirmed ? (
            <Button disabled={saving} onClick={startOwn} sx={actionSx}>Write my own</Button>
          ) : null}
          <IconButton
            disabled={saving}
            aria-label={`More actions for ${question.prompt}`}
            onClick={(event) => setMenuAnchor(event.currentTarget)}
            size="small"
            sx={{ ml: 'auto' }}
          >
            <MoreHorizRounded />
          </IconButton>
          <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
            {(suggestedText || selectedPoints.length || isConfirmed) ? (
              <MenuItem onClick={() => closeMenuThen(startEdit)}>Edit</MenuItem>
            ) : null}
            <MenuItem onClick={() => closeMenuThen(startOwn)}>Write my own</MenuItem>
            {customMenuSourceIds.length || selectedSourceIds.length ? (
              <MenuItem onClick={() => closeMenuThen(() => onViewSources(selectedSourceIds.length ? selectedSourceIds : customMenuSourceIds))}>View sources</MenuItem>
            ) : null}
            <MenuItem onClick={() => closeMenuThen(() => onSkip({ questionId: question.id, proposed: currentProposed }))}>Leave out</MenuItem>
          </Menu>
        </Stack>
      ) : canEdit ? (
        <Stack
          direction="row"
          useFlexGap
          flexWrap="wrap"
          spacing={isCustomCard ? { xs: 0.55, md: 0.9 } : 0.35}
          sx={isCustomCard ? {
            mt: { xs: 1.35, md: 1.6 },
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(5, minmax(0, auto))' },
            justifyContent: { sm: 'space-between' },
            '& .MuiButton-root': { justifyContent: { xs: 'flex-start', sm: 'center' }, px: { xs: 0.5, sm: 1 } },
          } : { mt: 1 }}
        >
          {!isConfirmed && !isSkipped && (suggestedText || selectedPoints.length) ? (
            <Button disabled={Boolean(saving || (points.length && !selectedPoints.length))} onClick={points.length ? confirmPoints : () => onConfirmSuggested({
              questionId: question.id, proposed: currentProposed, text: suggestedText, sourceIds: currentSourceIds,
            })} sx={actionSx}>Use this</Button>
          ) : null}
          {(suggestedText || selectedPoints.length || isConfirmed) ? <Button disabled={saving} onClick={startEdit} sx={actionSx}>Edit</Button> : null}
          <Button disabled={saving} onClick={() => onSkip({ questionId: question.id, proposed: currentProposed })} sx={{ ...actionSx, color: alpha(colors.semantic.ink, 0.62) }}>Leave out</Button>
          <Button disabled={saving} onClick={startOwn} sx={actionSx}>Write my own</Button>
          {defaultSourceIds.length || selectedSourceIds.length ? (
            <Button disabled={saving} onClick={() => onViewSources(selectedSourceIds.length ? selectedSourceIds : defaultSourceIds)} sx={actionSx}>View sources</Button>
          ) : null}
        </Stack>
      ) : null}
    </Box>
  );
};

export default GuidedPreparationQuestion;
